/**
 * Country dropdown entry built from the `countries` translation map.
 */
export interface CountryOption {
    code: string;
    name: string;
}

/**
 * Zone codes of the `countries` translation map, in display order. Every country dropdown lists
 * them first; countries and territories follow, sorted by name. `EU` is the Europe-wide code the
 * emission-factor engine falls back to, and the one stored when an emission factor has no country.
 */
export const COUNTRY_ZONE_CODES: readonly string[] = ['GLO', 'EU', 'EU27', 'RAS', 'RNA', 'RLA', 'RME', 'RAF', 'OCE'];

/**
 * Build the country dropdown options: zones first (in {@link COUNTRY_ZONE_CODES} order), then
 * countries and territories sorted by name with the collation of `lang`.
 *
 * @param countryNames the `countries` translation object, as returned by `TranslateService.get('countries')`
 * @param lang the current UI language, used for the alphabetical sort
 */
export function buildCountryOptions(countryNames: unknown, lang: string): CountryOption[] {
    if (typeof countryNames !== 'object' || countryNames === null) {
        return [];
    }

    const options = Object.entries(countryNames).flatMap(([code, name]) =>
        typeof name === 'string' ? [{ code, name }] : [],
    );
    const zones = COUNTRY_ZONE_CODES.flatMap((code) => options.find((option) => option.code === code) ?? []);
    const collator = new Intl.Collator(lang);
    const countries = options
        .filter((option) => !COUNTRY_ZONE_CODES.includes(option.code))
        .sort((a, b) => collator.compare(a.name, b.name));

    return [...zones, ...countries];
}
